// src/render/audio/ambient.ts — Phase 6 ambient night-bed loop (06-06).
//
// CONTEXT D-21: a single looping crickets+wind+distant-dogs layer that never
// dead-airs. Asset: `ambient/night-bed` (manifest key); ~30–90s loop length;
// budget ~150–500 KB. Howler looping bed, fade-in over P6_AMBIENT_FADE_IN_MS,
// volume P6_AMBIENT_VOLUME.
//
// AUDIO-03 invariant: the ambient bed plays continuously from initAmbient()
// onward — Howler's looping flag ensures the buffer restarts at the loop
// point without gap. The bed never dead-airs in v1; round transitions don't
// pause the bed (only `music.ts` swaps loops on round-started/round-ended).
//
// BLOCKER-1 (Plan 03 trackHowl contract): the constructed ambient Howl is
// registered with audioManager.trackHowl so audioManager.getLiveSources()
// returns a real count of active playback ids — used by 06-08
// phase6-integration.test.ts for the AUDIO-07 ≤ 8 concurrent ceiling proof.
// Cycle-safe at runtime: this file imports `audioManager` from manager.ts;
// manager.ts imports `initAmbient` from this file. The cycle is safe because
// `audioManager` is only dereferenced INSIDE initAmbient's body (never at
// module top-level), and by the time initAmbient runs, the manager singleton
// is fully constructed (manager.init() calls initAmbient synchronously).
//
// Tier-isolation: this module imports types only from `./codec` (render-side
// AudioCodec + AudioManifest). No imports from src/sim/** allowed.
//
// Source: 06-CONTEXT.md D-21; 06-PATTERNS.md `src/render/audio/ambient.ts`
// row; 06-RESEARCH.md Pattern 2 (looping ambient bed).

import { Howl } from 'howler';

import type { AudioCodec, AudioManifest } from './codec';
import { srcListFor } from './codec';
import { audioManager } from './manager';
import {
  P6_AMBIENT_VOLUME,
  P6_AMBIENT_FADE_IN_MS,
} from './constants';

// ---------------------------------------------------------------------------
// Module-level state (singleton — D-21)
// ---------------------------------------------------------------------------

let ambientHowl: Howl | null = null;

// ---------------------------------------------------------------------------
// Public API (matches 06-03 manager.ts seam)
// ---------------------------------------------------------------------------

export function initAmbient(_codec: AudioCodec, manifest: AudioManifest): void {
  ambientHowl = new Howl({
    src: srcListFor('ambient/night-bed', manifest),
    loop: true,
    pool: 1,           // D-20 — single ambient voice
    preload: false,    // P6-Pitfall 8 — load only after iOS unlock; load() called below
    volume: 0,         // start silent; fade in
  });
  ambientHowl.load();
  // BLOCKER-1: register the ambient Howl with manager liveSources Set
  // (Plan 03 trackHowl contract). Cycle-safe at runtime — see header comment.
  // UI-06: the night-bed lives on the 'sfx' bus (D-01/A2 — "Music" silences
  // only the score; the world stays alive) at design volume P6_AMBIENT_VOLUME.
  audioManager.trackHowl(ambientHowl, 'sfx', P6_AMBIENT_VOLUME);
  ambientHowl.play();
  // UI-06: fade target scaled by the live SFX bus gain (Pitfall A minor).
  ambientHowl.fade(0, P6_AMBIENT_VOLUME * audioManager.getBusGain('sfx'), P6_AMBIENT_FADE_IN_MS);
}

export function destroyAmbient(): void {
  ambientHowl?.unload();
  ambientHowl = null;
}
